'use client'

import { useTheme } from '@/contexts/ThemeContext'
import { Users, Target, Shield, Heart } from 'lucide-react'

export default function AboutPage() {
  const { theme } = useTheme()

  const values = [
    {
      icon: <Shield className={`w-8 h-8 ${theme === 'light' ? 'text-blue-500' : 'text-blue-400'}`} />,
      title: '안전',
      description: '다이버의 안전을 최우선으로 하는 매칭 시스템과 안전 교육을 제공합니다.'
    },
    {
      icon: <Users className={`w-8 h-8 ${theme === 'light' ? 'text-blue-500' : 'text-blue-400'}`} />,
      title: '신뢰',
      description: '검증된 다이버들과의 매칭을 통해 신뢰할 수 있는 버디 관계를 형성합니다.'
    },
    {
      icon: <Target className={`w-8 h-8 ${theme === 'light' ? 'text-blue-500' : 'text-blue-400'}`} />,
      title: '정확성',
      description: '개인의 실력과 선호도를 정확히 분석하여 최적의 매칭을 제공합니다.'
    },
    {
      icon: <Heart className={`w-8 h-8 ${theme === 'light' ? 'text-blue-500' : 'text-blue-400'}`} />,
      title: '열정',
      description: '바다를 사랑하는 모든 다이버들의 꿈을 응원하고 지원합니다.'
    }
  ]

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 헤더 섹션 */}
        <div className="text-center mb-16 xxs:mb-8">
          <h1 className={`text-4xl xxs:text-2xl md:text-5xl font-bold mb-6 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              오늘다이빙
            </span>
          </h1>
          <p className={`text-xl xxs:text-base max-w-3xl mx-auto ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            안전하고 신뢰할 수 있는 프리다이빙 버디 매칭 플랫폼으로,
            바다를 사랑하는 모든 다이버들을 연결합니다.
          </p>
        </div>

        {/* 미션 섹션 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-6 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            우리의 미션
          </h2>
          <p className={`text-lg xxs:text-sm text-center max-w-4xl mx-auto ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            프리다이빙을 사랑하는 모든 사람들이 안전하고 즐겁운 다이빙을 할 수 있도록,
            신뢰할 수 있는 버디를 찾아주는 것입니다. 우리는 기술을 통해 다이버들 간의
            연결을 돕고, 안전한 다이빙 문화를 만들어가고자 합니다.
          </p>
        </div>

        {/* 핵심 가치 */}
        <div className="mb-16 xxs:mb-8">
          <h2 className={`text-3xl xxs:text-xl font-bold mb-8 xxs:mb-4 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            핵심 가치
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 xxs:gap-3">
            {values.map((value, index) => (
              <div
                key={index}
                className={`p-6 xxs:p-3 rounded-xl text-center ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}
              >
                <div className="flex justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className={`text-xl xxs:text-base font-semibold mb-3 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                  {value.title}
                </h3>
                <p className={`${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 스토리 섹션 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-6 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            우리의 이야기
          </h2>
          <div className={`space-y-6 text-lg xxs:text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            <p>
              오늘다이빙은 프리다이빙을 사랑하는 한 사람의 고민으로부터 시작된 프로젝트입니다.
              그 사람은 버디를 찾기 어려운 현실을 경험했고,
              이를 해결하기 위한 플랫폼을 만들게 되었습니다.
            </p>
            <p>
              2025년 12월 1일부터 시작된 이 프로젝트는 다이버들의 안전과 편의를 위해 지속적으로 발전하겠습니다.
              실력, 경험, 선호도를 기반으로 한 정확한 매칭 시스템과 실시간 커뮤니케이션 기능을 통해
              더 많은 다이버들이 안전하고 즐거운 다이빙을 경험할 수 있도록 발전해 가겠습니다.
            </p>
          </div>
        </div>

        {/* 팀 정보 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-6 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            팀 소개
          </h2>
          <p className={`text-lg xxs:text-sm text-center max-w-4xl mx-auto ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            프리다이빙에 대한 열정과 기술에 대한 전문성을 가진 팀이
            안전하고 혁신적인 다이빙 매칭 서비스를 만들어가고 있습니다.
            다이버의 관점에서 실제 필요한 기능들을 개발하고 있으며,
            사용자 피드백을 바탕으로 지속적으로 서비스를 개선하고 있습니다.
          </p>
        </div>

        {/* 연락처 */}
        <div className={`text-center p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'}`}>
          <h2 className={`text-2xl xxs:text-lg font-bold mb-4 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            함께 만들어가요
          </h2>
          <p className={`mb-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            더 나은 다이빙 문화를 만들어가는 데 함께하고 싶으시다면 언제든 연락해주세요.
          </p>
          <div className="space-y-2">
            <p className={`${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
              이메일: 0wonchorok@naver.com	
            </p>
            <p className={`${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
              Instagram: @today_diving
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}